import { getToastExecutionBindings } from "./execution-bindings";
import { dismissSimpleToast, showSimpleToast } from "./simple-toast-store";
import type {
  ExecutionHandle,
  SimpleToastOptions,
  ToastId,
  TrackExecutionOptions,
} from "./types";

function assertExecutionProviderMounted(methodName: string): void {
  const message =
    `${methodName}() called before ToastProvider mounted. ` +
    "Mount <ToastProvider /> from ~/lib/toast/provider inside app-providers.";

  if (process.env.NODE_ENV === "development") {
    throw new Error(message);
  }

  console.warn(message);
}

export const toast = {
  dismiss(id?: ToastId) {
    if (id !== undefined) {
      getToastExecutionBindings()?.dismissExecution(String(id));
    }

    dismissSimpleToast(id);
  },

  dismissExecution(operationId: string) {
    const executionBindings = getToastExecutionBindings();
    if (!executionBindings) {
      assertExecutionProviderMounted("toast.dismissExecution");
      return;
    }

    executionBindings.dismissExecution(operationId);
  },

  error(message: string, options?: SimpleToastOptions) {
    return showSimpleToast("error", message, options);
  },

  info(message: string, options?: SimpleToastOptions) {
    return showSimpleToast("info", message, options);
  },

  loading(message: string, options?: SimpleToastOptions) {
    return showSimpleToast("loading", message, options);
  },

  openExecution(operationId: string) {
    const executionBindings = getToastExecutionBindings();
    if (!executionBindings) {
      assertExecutionProviderMounted("toast.openExecution");
      return;
    }

    executionBindings.openExecution(operationId);
  },

  success(message: string, options?: SimpleToastOptions) {
    return showSimpleToast("success", message, options);
  },

  trackExecution(handle: ExecutionHandle, options?: TrackExecutionOptions) {
    // replaceToastId targets a transitional simple toast for the same action.
    if (options?.replaceToastId !== undefined) {
      dismissSimpleToast(options.replaceToastId);
    }

    const executionBindings = getToastExecutionBindings();
    if (!executionBindings) {
      assertExecutionProviderMounted("toast.trackExecution");
      return handle.operationId;
    }

    return executionBindings.trackExecution(handle, options);
  },

  warning(message: string, options?: SimpleToastOptions) {
    return showSimpleToast("warning", message, options);
  },
};
